'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Award, Edit, ShoppingCart, FileText, Phone, Mail, Globe, MapPin, Truck, Shield, BarChart3, CheckCircle2, Clock, TrendingUp, DollarSign, ChevronRight, Plus, Download, Flag,  } from 'lucide-react';
import {
  MOCK_SUPPLIERS, MOCK_EVALUATIONS, MOCK_INCIDENTS, MOCK_PAYMENTS, MOCK_CONTRACTS,
  SUPPLIER_STATUS_STYLES, COMPLIANCE_STATUS_STYLES, formatUGX, getPerformanceLevel,
  SupplierStatus, ComplianceStatus,
} from '../../components/suppliersData';
import { useRole } from '@/lib/useRole';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import Icon from '@/components/ui/AppIcon';


// ─── Badges ───────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: SupplierStatus }) {
  const st = SUPPLIER_STATUS_STYLES[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
      {st.label}
    </span>
  );
}

function ComplianceBadge({ status }: { status: ComplianceStatus }) {
  const st = COMPLIANCE_STATUS_STYLES[status];
  return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>{st.label}</span>;
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, icon: Icon, color }: { label: string; value: string | number; sub?: string; icon: React.ElementType; color: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${color}`}>
        <Icon size={14} className="text-white" />
      </div>
      <div className="text-lg font-black text-gray-900">{value}</div>
      <div className="text-xs font-semibold text-gray-500">{label}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

// ─── Tab Button ───────────────────────────────────────────────────────────────

function TabBtn({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${active ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
    >
      {label}
    </button>
  );
}

// ─── Price History ────────────────────────────────────────────────────────────

const PRICE_HISTORY = [
  { month: 'Apr', price: 118000 },
  { month: 'May', price: 120000 },
  { month: 'Jun', price: 119000 },
  { month: 'Jul', price: 122000 },
  { month: 'Aug', price: 125000 },
  { month: 'Sep', price: 121000 },
];

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SupplierProfileContent({ id }: { id: string }) {
  const { can } = useRole();
  const canManage = can('manage_procurement');
  const [activeTab, setActiveTab] = useState('overview');

  const supplier = MOCK_SUPPLIERS.find(s => s.id === id) ?? MOCK_SUPPLIERS[0];
  const evaluations = MOCK_EVALUATIONS.filter(e => e.supplierId === supplier.id);
  const incidents = MOCK_INCIDENTS.filter(i => i.supplierId === supplier.id);
  const payments = MOCK_PAYMENTS.filter(p => p.supplierId === supplier.id);
  const contracts = MOCK_CONTRACTS.filter(c => c.supplierId === supplier.id);
  const primaryContact = supplier.contacts?.find(c => c.isPrimary);

  const perf = supplier.performance;
  const perfLevel = getPerformanceLevel(supplier.overallScore ?? 0);

  const radarData = perf ? [
    { subject: 'Quality', score: perf.qualityScore },
    { subject: 'Delivery', score: perf.deliveryScore },
    { subject: 'Price', score: perf.priceScore },
    { subject: 'Compliance', score: perf.complianceScore },
    { subject: 'Communication', score: perf.communicationScore },
    { subject: 'Support', score: perf.supportScore },
  ] : [];

  const TABS = ['overview', 'contacts', 'products', 'contracts', 'documents', 'performance', 'evaluations', 'incidents', 'payments', 'audit'];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-3 mb-4">
          <Link href="/suppliers" className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <ArrowLeft size={16} className="text-gray-500" />
          </Link>
          <span className="text-xs text-gray-400">Suppliers</span>
          <ChevronRight size={12} className="text-gray-300" />
          <span className="text-xs text-gray-600 font-medium">{supplier.name}</span>
        </div>

        {/* Supplier Header */}
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center text-2xl font-black text-orange-600 flex-shrink-0">
            {supplier.name.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-black text-gray-900">{supplier.name}</h1>
              {supplier.preferred && (
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-700">
                  <Award size={11} />Preferred
                </span>
              )}
              <StatusBadge status={supplier.status} />
            </div>
            <div className="flex items-center gap-3 mt-1 flex-wrap text-xs text-gray-500">
              <span className="font-semibold text-orange-600">{supplier.supplierCode}</span>
              <span>·</span>
              <span>{supplier.supplierType}</span>
              <span>·</span>
              <span className="flex items-center gap-1"><MapPin size={11} />{supplier.city}, {supplier.country}</span>
              <span>·</span>
              <ComplianceBadge status={supplier.complianceStatus} />
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {supplier.categories.map(c => (
                <span key={c} className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full">{c}</span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {canManage && (
              <Link href={`/suppliers/${supplier.id}`} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
                <Edit size={13} />Edit
              </Link>
            )}
            <Link href="/procurement/rfqs/new" className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
              <FileText size={13} />Create RFQ
            </Link>
            <Link href="/procurement/purchase-orders/new" className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors">
              <ShoppingCart size={13} />Create PO
            </Link>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="px-6 py-4 bg-white border-b border-gray-100">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          <StatCard label="Total Spend" value={`UGX ${(supplier.totalSpend / 1000000).toFixed(1)}M`} icon={DollarSign} color="bg-blue-500" />
          <StatCard label="Outstanding" value={formatUGX(supplier.outstandingBalance)} icon={TrendingUp} color="bg-orange-500" />
          <StatCard label="Open POs" value={supplier.openPOs} icon={ShoppingCart} color="bg-teal-500" />
          <StatCard label="On-Time Delivery" value={`${supplier.onTimeDeliveryPct ?? 0}%`} icon={Truck} color="bg-green-500" />
          <StatCard label="Quality Score" value={`${supplier.qualityScore ?? 0}%`} icon={CheckCircle2} color="bg-purple-500" />
          <StatCard label="Overall Score" value={supplier.overallScore ?? 0} sub={perfLevel.label} icon={BarChart3} color="bg-gray-700" />
          <StatCard label="Rating" value={`${supplier.rating ?? 0}/5`} icon={Star} color="bg-amber-500" />
          <StatCard label="Last Purchase" value={supplier.lastPurchaseDate ?? '—'} icon={Clock} color="bg-gray-500" />
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-6 overflow-x-auto">
        <div className="flex">
          {TABS.map(t => (
            <TabBtn key={t} label={t.charAt(0).toUpperCase() + t.slice(1)} active={activeTab === t} onClick={() => setActiveTab(t)} />
          ))}
        </div>
      </div>

      <div className="p-6 max-w-screen-xl mx-auto">

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Company Info */}
            <div className="lg:col-span-2 space-y-5">
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-4">Company Information</h3>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  {[
                    ['Supplier Code', supplier.supplierCode],
                    ['Supplier Type', supplier.supplierType],
                    ['Registration No.', supplier.registrationNumber ?? '—'],
                    ['TIN / Tax No.', supplier.taxNumber ?? '—'],
                    ['Country', supplier.country],
                    ['City', supplier.city ?? '—'],
                    ['Payment Terms', supplier.paymentTerms],
                    ['Credit Days', supplier.creditDays ? `${supplier.creditDays} days` : '—'],
                    ['Credit Limit', supplier.creditLimit ? formatUGX(supplier.creditLimit) : '—'],
                    ['Currency', supplier.currency],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <div className="text-gray-400 mb-0.5">{label}</div>
                      <div className="font-semibold text-gray-700">{value}</div>
                    </div>
                  ))}
                </div>
                {supplier.physicalAddress && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="text-xs text-gray-400 mb-0.5">Physical Address</div>
                    <div className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                      <MapPin size={11} className="text-gray-400" />
                      {supplier.physicalAddress}
                    </div>
                  </div>
                )}
                <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-4 text-xs">
                  {supplier.email && (
                    <a href={`mailto:${supplier.email}`} className="flex items-center gap-1.5 text-orange-600 hover:underline">
                      <Mail size={11} />{supplier.email}
                    </a>
                  )}
                  {supplier.phone && (
                    <a href={`tel:${supplier.phone}`} className="flex items-center gap-1.5 text-gray-600">
                      <Phone size={11} />{supplier.phone}
                    </a>
                  )}
                  {supplier.website && (
                    <a href={`https://${supplier.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-gray-600">
                      <Globe size={11} />{supplier.website}
                    </a>
                  )}
                </div>
              </div>

              {/* Price History Chart */}
              {supplier.products && supplier.products.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-200 p-5">
                  <h3 className="text-sm font-bold text-gray-900 mb-1">Price Trend — {supplier.products[0].productName}</h3>
                  <p className="text-xs text-gray-400 mb-4">Historical unit price (UGX)</p>
                  <ResponsiveContainer width="100%" height={160}>
                    <AreaChart data={PRICE_HISTORY}>
                      <defs>
                        <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f97316" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                      <Tooltip formatter={(v: number) => [formatUGX(v), 'Unit Price']} />
                      <Area type="monotone" dataKey="price" stroke="#f97316" strokeWidth={2} fill="url(#priceGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              {/* Primary Contact */}
              {primaryContact && (
                <div className="bg-white rounded-2xl border border-gray-200 p-4">
                  <h3 className="text-xs font-bold text-gray-700 mb-3">Primary Contact</h3>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center text-sm font-black text-orange-600">
                      {primaryContact.fullName.charAt(0)}
                    </div>
                    <div>
                      <div className="text-xs font-bold text-gray-900">{primaryContact.fullName}</div>
                      <div className="text-xs text-gray-400">{primaryContact.jobTitle}</div>
                      {primaryContact.phone && <div className="text-xs text-gray-600 mt-1 flex items-center gap-1"><Phone size={10} />{primaryContact.phone}</div>}
                      {primaryContact.email && <div className="text-xs text-orange-600 mt-0.5 flex items-center gap-1"><Mail size={10} />{primaryContact.email}</div>}
                    </div>
                  </div>
                </div>
              )}

              {/* Compliance Status */}
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <h3 className="text-xs font-bold text-gray-700 mb-3">Document Compliance</h3>
                <div className="space-y-2">
                  {supplier.documents?.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between">
                      <div className="text-xs text-gray-600 truncate flex-1">{doc.documentType}</div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ml-2 ${
                        doc.status === 'valid' ? 'bg-green-50 text-green-700' :
                        doc.status === 'expiring_soon' ? 'bg-amber-50 text-amber-700' :
                        doc.status === 'expired'? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {doc.status === 'valid' ? 'Valid' : doc.status === 'expiring_soon' ? 'Expiring' : doc.status === 'expired' ? 'Expired' : 'Pending'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Active Contracts */}
              {contracts.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-200 p-4">
                  <h3 className="text-xs font-bold text-gray-700 mb-3">Active Contracts</h3>
                  {contracts.map(c => (
                    <div key={c.id} className="text-xs">
                      <div className="font-semibold text-gray-800">{c.contractNumber}</div>
                      <div className="text-gray-400">{c.contractType}</div>
                      <div className="text-gray-500 mt-0.5">{c.startDate} → {c.endDate}</div>
                      <div className="font-bold text-orange-600 mt-0.5">{formatUGX(c.value)}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Quick Actions */}
              {canManage && (
                <div className="bg-white rounded-2xl border border-gray-200 p-4">
                  <h3 className="text-xs font-bold text-gray-700 mb-3">Quick Actions</h3>
                  <div className="space-y-2">
                    <Link href="/procurement/rfqs/new" className="flex items-center gap-2 w-full px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <FileText size={12} className="text-orange-500" />Create RFQ
                    </Link>
                    <Link href="/procurement/purchase-orders/new" className="flex items-center gap-2 w-full px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <ShoppingCart size={12} className="text-orange-500" />Create PO
                    </Link>
                    <button onClick={() => setActiveTab('evaluations')} className="flex items-center gap-2 w-full px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <BarChart3 size={12} className="text-orange-500" />Evaluate Supplier
                    </button>
                    <button onClick={() => setActiveTab('incidents')} className="flex items-center gap-2 w-full px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <Flag size={12} className="text-red-500" />Record Incident
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CONTACTS TAB */}
        {activeTab === 'contacts' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">Contact Persons</h3>
              {canManage && (
                <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors">
                  <Plus size={12} />Add Contact
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {supplier.contacts?.map(c => (
                <div key={c.id} className="bg-white rounded-2xl border border-gray-200 p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center text-sm font-black text-orange-600">
                      {c.fullName.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold text-gray-900">{c.fullName}</span>
                        {c.isPrimary && <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">Primary</span>}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">{c.jobTitle}</div>
                      {c.phone && <div className="text-xs text-gray-600 mt-2 flex items-center gap-1"><Phone size={10} />{c.phone}</div>}
                      {c.email && <div className="text-xs text-orange-600 mt-0.5 flex items-center gap-1"><Mail size={10} />{c.email}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PRODUCTS TAB */}
        {activeTab === 'products' && (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">Products & Services Catalog</h3>
              {canManage && (
                <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors">
                  <Plus size={12} />Add Product
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-5 py-3 font-semibold text-gray-500">Product / Service</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500">Category</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500">Brand</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500">Unit</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-500">Standard Price</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-500">Last Price</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-500">Lead Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {supplier.products?.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50/50">
                      <td className="px-5 py-3">
                        <div className="font-semibold text-gray-900">{p.productName}</div>
                        {p.supplierCode && <div className="text-gray-400">{p.supplierCode}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{p.category}</td>
                      <td className="px-4 py-3 text-gray-600">{p.brand ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{p.unit}</td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-700">{formatUGX(p.standardPrice)}</td>
                      <td className="px-4 py-3 text-right">
                        {p.lastPurchasePrice ? (
                          <div>
                            <div className="font-semibold text-gray-700">{formatUGX(p.lastPurchasePrice)}</div>
                            {p.lastPurchaseDate && <div className="text-gray-400">{p.lastPurchaseDate}</div>}
                          </div>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600">{p.leadTimeDays ? `${p.leadTimeDays} days` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!supplier.products || supplier.products.length === 0) && (
                <div className="text-center py-10 text-xs text-gray-400">No products listed for this supplier.</div>
              )}
            </div>
          </div>
        )}

        {/* PERFORMANCE TAB */}
        {activeTab === 'performance' && perf && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Radar Chart */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h3 className="text-sm font-bold text-gray-900 mb-4">Performance Scorecard</h3>
              <ResponsiveContainer width="100%" height={260}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#f3f4f6" />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                  <Radar name="Score" dataKey="score" stroke="#f97316" fill="#f97316" fillOpacity={0.15} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Score Breakdown */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h3 className="text-sm font-bold text-gray-900 mb-4">Score Breakdown</h3>
              <div className="space-y-3">
                {[
                  { label: 'Quality', score: perf.qualityScore, weight: 30 },
                  { label: 'Delivery Timeliness', score: perf.deliveryScore, weight: 25 },
                  { label: 'Price Competitiveness', score: perf.priceScore, weight: 20 },
                  { label: 'Specification Compliance', score: perf.complianceScore, weight: 10 },
                  { label: 'Invoice Accuracy', score: perf.invoiceAccuracyScore, weight: 5 },
                  { label: 'Communication', score: perf.communicationScore, weight: 5 },
                  { label: 'Service / Support', score: perf.supportScore, weight: 5 },
                ].map(item => {
                  const perf2 = getPerformanceLevel(item.score);
                  return (
                    <div key={item.label}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-gray-700">{item.label}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">{item.weight}%</span>
                          <span className={`text-xs font-bold ${perf2.color}`}>{item.score}</span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full bg-orange-400 transition-all" style={{ width: `${item.score}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                <span className="text-sm font-bold text-gray-900">Overall Score</span>
                <div className="flex items-center gap-2">
                  <span className={`text-xl font-black ${perfLevel.color}`}>{perf.overallScore}</span>
                  <span className={`text-xs font-semibold ${perfLevel.color}`}>{perfLevel.label}</span>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3 text-center">
                <div className="bg-gray-50 rounded-xl p-3">
                  <div className="text-lg font-black text-gray-900">{perf.onTimeDeliveryPct}%</div>
                  <div className="text-xs text-gray-400">On-Time Delivery</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <div className="text-lg font-black text-gray-900">{perf.acceptanceRate}%</div>
                  <div className="text-xs text-gray-400">Acceptance Rate</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <div className="text-lg font-black text-gray-900">{perf.totalOrders}</div>
                  <div className="text-xs text-gray-400">Total Orders</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* EVALUATIONS TAB */}
        {activeTab === 'evaluations' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">Supplier Evaluations</h3>
              {canManage && (
                <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors">
                  <Plus size={12} />New Evaluation
                </button>
              )}
            </div>
            {evaluations.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
                <BarChart3 size={32} className="text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No evaluations recorded yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {evaluations.map(ev => (
                  <div key={ev.id} className="bg-white rounded-2xl border border-gray-200 p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="text-sm font-bold text-gray-900">{ev.period}</div>
                        <div className="text-xs text-gray-400">Evaluated by {ev.evaluator} · {ev.date}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-lg font-black ${getPerformanceLevel(ev.overallScore).color}`}>{ev.overallScore}</span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ev.status === 'approved' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                          {ev.status}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 text-center text-xs">
                      {[
                        ['Quality', ev.qualityScore],
                        ['Delivery', ev.deliveryScore],
                        ['Price', ev.priceScore],
                        ['Compliance', ev.complianceScore],
                        ['Communication', ev.communicationScore],
                        ['Support', ev.supportScore],
                      ].map(([label, score]) => (
                        <div key={label} className="bg-gray-50 rounded-xl p-2">
                          <div className={`text-base font-black ${getPerformanceLevel(score as number).color}`}>{score}</div>
                          <div className="text-gray-400">{label}</div>
                        </div>
                      ))}
                    </div>
                    {ev.comments && <p className="text-xs text-gray-600 mt-3 bg-gray-50 rounded-xl p-3">{ev.comments}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* INCIDENTS TAB */}
        {activeTab === 'incidents' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">Supplier Incidents</h3>
              {canManage && (
                <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors">
                  <Plus size={12} />Record Incident
                </button>
              )}
            </div>
            {incidents.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
                <Shield size={32} className="text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No incidents recorded for this supplier.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {incidents.map(inc => (
                  <div key={inc.id} className="bg-white rounded-2xl border border-gray-200 p-5">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="text-xs font-bold text-gray-900">{inc.incidentNumber} — {inc.incidentType}</div>
                        <div className="text-xs text-gray-400">{inc.date} · Reported by {inc.reportedBy}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${inc.severity === 'critical' ? 'bg-red-100 text-red-700' : inc.severity === 'high' ? 'bg-orange-100 text-orange-700' : inc.severity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                          {inc.severity.toUpperCase()}
                        </span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${inc.status === 'resolved' || inc.status === 'closed' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                          {inc.status}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600">{inc.description}</p>
                    {inc.correctiveAction && (
                      <div className="mt-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                        <span className="font-semibold">Corrective Action:</span> {inc.correctiveAction}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PAYMENTS TAB */}
        {activeTab === 'payments' && (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">Payment History</h3>
              <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
                <Download size={12} />Export
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-5 py-3 font-semibold text-gray-500">Payment No.</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500">Invoice Ref</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500">Date</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-500">Amount</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500">Method</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500">Reference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {payments.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50/50">
                      <td className="px-5 py-3 font-semibold text-gray-800">{p.paymentNumber}</td>
                      <td className="px-4 py-3 text-gray-600">{p.invoiceRef}</td>
                      <td className="px-4 py-3 text-gray-600">{p.paymentDate}</td>
                      <td className="px-4 py-3 text-right font-bold text-gray-800">{formatUGX(p.amount)}</td>
                      <td className="px-4 py-3 text-gray-600">{p.paymentMethod}</td>
                      <td className="px-4 py-3 text-gray-400">{p.reference}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {payments.length === 0 && (
                <div className="text-center py-10 text-xs text-gray-400">No payments recorded.</div>
              )}
            </div>
          </div>
        )}

        {/* CONTRACTS TAB */}
        {activeTab === 'contracts' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">Contracts</h3>
              {canManage && (
                <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors">
                  <Plus size={12} />New Contract
                </button>
              )}
            </div>
            {contracts.map(c => (
              <div key={c.id} className="bg-white rounded-2xl border border-gray-200 p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-sm font-bold text-gray-900">{c.contractNumber}</div>
                    <div className="text-xs text-gray-400">{c.contractType} · Owner: {c.owner}</div>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${c.status === 'active' ? 'bg-green-50 text-green-700' : c.status === 'expiring' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                    {c.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div><div className="text-gray-400">Start Date</div><div className="font-semibold text-gray-700">{c.startDate}</div></div>
                  <div><div className="text-gray-400">End Date</div><div className="font-semibold text-gray-700">{c.endDate}</div></div>
                  <div><div className="text-gray-400">Contract Value</div><div className="font-bold text-orange-600">{formatUGX(c.value)}</div></div>
                  <div><div className="text-gray-400">Payment Terms</div><div className="font-semibold text-gray-700">{c.paymentTerms}</div></div>
                </div>
              </div>
            ))}
            {contracts.length === 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
                <FileText size={32} className="text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No contracts for this supplier.</p>
              </div>
            )}
          </div>
        )}

        {/* DOCUMENTS TAB */}
        {activeTab === 'documents' && (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">Compliance Documents</h3>
              {canManage && (
                <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors">
                  <Plus size={12} />Upload Document
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-5 py-3 font-semibold text-gray-500">Document Type</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500">Document No.</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500">Issue Date</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500">Expiry Date</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {supplier.documents?.map(doc => (
                    <tr key={doc.id} className="hover:bg-gray-50/50">
                      <td className="px-5 py-3 font-semibold text-gray-800">{doc.documentType}</td>
                      <td className="px-4 py-3 text-gray-600">{doc.documentNumber ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{doc.issueDate ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{doc.expiryDate ?? '—'}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          doc.status === 'valid' ? 'bg-green-50 text-green-700' :
                          doc.status === 'expiring_soon' ? 'bg-amber-50 text-amber-700' :
                          doc.status === 'expired'? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {doc.status === 'valid' ? 'Valid' : doc.status === 'expiring_soon' ? 'Expiring Soon' : doc.status === 'expired' ? 'Expired' : 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* AUDIT TAB */}
        {activeTab === 'audit' && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Audit Trail</h3>
            <div className="space-y-3">
              {[
                { action: 'Supplier Created', user: 'Okello James', date: supplier.createdAt.split('T')[0], detail: 'Supplier registered in the system' },
                { action: 'Status Updated', user: 'Mugisha Robert', date: '2026-01-20', detail: `Status changed to ${supplier.status}` },
                { action: 'Contract Created', user: 'Mugisha Robert', date: '2026-01-01', detail: 'Framework agreement CON-2026-0001 created' },
                { action: 'Evaluation Completed', user: 'Mugisha Robert', date: '2026-07-05', detail: 'Q2 2026 evaluation submitted and approved' },
              ].map((entry, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-orange-400 mt-1.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-gray-800">{entry.action}</span>
                      <span className="text-xs text-gray-400">{entry.date}</span>
                    </div>
                    <div className="text-xs text-gray-500">{entry.detail}</div>
                    <div className="text-xs text-gray-400">by {entry.user}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
